import React, {useEffect, useState, useMemo, useRef} from 'react';
import {GridSortModel} from '@mui/x-data-grid-pro';
import {useLocation, useSearchParams} from 'react-router-dom';
import StandardReports from '../../StandardReports';
import {t} from 'i18next';
import {
  mergeApiDataIntoConfig,
  getUserActivityData,
} from '../../common/ReportHelper';
import {ISelectItem} from '../../../../../shared/types';
import {
  useExportUserActivityReport,
  useGetUserActivityReportResultData,
  useSaveUserActivityReportData,
  useLeftPanelReport,
} from '../../../../../hooks/reporting/useReporting';
import {Reports, RightPanelConfig} from '../../config/reportTypes';
import {columns} from './column';
import dayjs from 'dayjs';
import {
  UserActivityReportRequestBody,
  UserActivityReportResponse,
  ExportUserActivityReportRequestPayload,
  UserActivitySaveRequestPayload,
} from '../../../../../api/types/reportingApi.type';
import {
  REPORT_PAGE_SIZE_OPTIONS,
  ToastType,
} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import ArrWaitToCompleteModalWrapper from '../../../standard-reports/component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import {
  getReportIdFromUrl,
  getReportNameFromUrl,
} from '../../../../../utils/reportUtils';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';

interface ReportConfig {
  infoContent?: string | string[];
  title: string;
  showInfoIcon?: boolean;
  config: Reports;
}

const DEFAULT_OPTION: ISelectItem = {
  value: 'currentLoggedIn',
  label: 'Currently Logged-In Users',
};

export interface ISavePayload {
  reportTypeCode: string;
  selectedReviewers: number[];
  fromDateTime: string | null;
  toDateTime: string | null;
  showCurrentlyLoggedInUsers: number;
  selectedCustomColumns: number[];
  selectedSource: string;
  pageNumber: number;
  pageSize: number;
  sortingColumn: string;
  isAscending: string;
  reportID: number;
}

const REPORT_KEY = 'UserActivity';
const REPORT_TYPE_CODE = 'UAR009';

const ToastMessages = {
  UsersFieldIsRequired: t(
    'docExplorer.actionMenu.bulkTaggingWarning.userActivityWarning'
  ),
  exportHasStarted: t('reports.common.reportExportSuccess'),
  exportHasFailed: t('reports.common.reportExportError'),
  UserFieldValidationMessage: 'Please select at least one user to get report',
  DateRangeValidationMessage: 'Please select a valid date range',
};

interface PageInfo {
  totalRecords: number;
  pageSize: number;
  pageNumber: number;
}

const UserActivityContainer: React.FC = () => {
  const [reportConfig, setReportConfig] = useState<ReportConfig | null>(null);
  const [dynamicData, setDynamicData] = useState<any>(null);
  const [saveReportPayload, setSaveReportPayload] = useState<ISavePayload>({
    reportTypeCode: '',
    selectedReviewers: [],
    fromDateTime: '',
    toDateTime: '',
    showCurrentlyLoggedInUsers: 0,
    selectedCustomColumns: [],
    selectedSource: '',
    pageNumber: 0,
    pageSize: 0,
    sortingColumn: '',
    isAscending: '',
    reportID: 0,
  });
  const [selectedOption, setSelectedOption] = useState<ISelectItem | null>(
    DEFAULT_OPTION
  );
  const [gridData, setGridData] = useState<UserActivityReportResponse | null>(
    null
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'dateTime',
      sort: 'desc',
    },
  ]);
  const [pageModel, setPageModel] = useState<PageInfo>({
    totalRecords: 0,
    pageSize: 10,
    pageNumber: 1,
  });
  // The initial results load is owned by the filter/report initialisation effect.
  const isFirstLoadRef = useRef(true);

  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [notificationId, setNotificationId] = useState<number | null>(null);
  const [reportId, setReportId] = useState<number | null>(null);
  const [hasAppliedOnce, setHasAppliedOnce] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any>>(
    {}
  );
  const [initialValues, setInitialValues] = useState<Record<string, any>>();
  const [isInitialized, setIsInitialized] = useState(false);
  const isWaitingForBackgroundJob = Boolean(notificationId && reportId);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {fromReportLanding = false} = location.state ?? {};
  const {mutateAsync: saveUserActivityReportData} =
    useSaveUserActivityReportData();
  const urlReportId = Number.parseInt(searchParams.get('ReportID') ?? '0');
  const {mutateAsync: getReportGridData} = useGetUserActivityReportResultData();
  const {mutateAsync: runUserActivityExport} = useExportUserActivityReport();
  const memoizedUrlReportId = useMemo(
    () => getReportIdFromUrl(window.location.search) ?? 0,
    [window.location.search]
  );
  const resolvedReportId =
    typeof urlReportId !== 'undefined' && urlReportId !== null
      ? urlReportId
      : memoizedUrlReportId;
  const {data: leftPanelReport} = useLeftPanelReport(resolvedReportId);

  useEffect(() => {
    (async function loadReportConfig() {
      try {
        const data = await import(`../../config/${REPORT_KEY}.json`);
        setReportConfig(data.report);
      } catch (e) {
        console.error('Error loading JSON:', e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!reportConfig) return;
    (async function loadDynamic() {
      try {
        const d = await getUserActivityData();
        setDynamicData(d);
      } catch (e) {
        console.error('Error fetching API data:', e);
      }
    })();
  }, [reportConfig]);

  useEffect(() => {
    if (!dynamicData) {
      return;
    }

    if (hasAppliedOnce) {
      return;
    }

    if (leftPanelReport) {
      const values = {
        fromDate: leftPanelReport.fromDateTime
          ? dayjs(leftPanelReport.fromDateTime).format('YYYY/MM/DD HH:mm:ss')
          : '',
        toDate: leftPanelReport.toDateTime
          ? dayjs(leftPanelReport.toDateTime).format('YYYY/MM/DD HH:mm:ss')
          : '',
        users: leftPanelReport.selected_UserID,
        usersList:
          dynamicData.userDetails?.map((a: any) =>
            leftPanelReport.selected_UserID.includes(a.userId)
          ) ?? [],
        actionTypes:
          dynamicData.actionTypes?.map((a: any) =>
            leftPanelReport.selected_ActionTypeID.includes(a.actionTypeID)
          ) ?? [],
      };

      setInitialValues(values);
      setSelectedFilters(values);
    } else {
      const today = dayjs().utc().format('YYYY/MM/DD HH:mm:ss');
      const oneWeekAgo = dayjs()
        .utc()
        .subtract(1, 'week')
        .format('YYYY/MM/DD HH:mm:ss');

      const defaultValues = {
        fromDate: oneWeekAgo,
        toDate: today,
        users: [],
        usersList: dynamicData.userDetails?.map(() => false) ?? [],
        actionTypes: dynamicData.actionTypes?.map(() => true) ?? [],
      };

      setInitialValues(defaultValues);
      setSelectedFilters(defaultValues);
    }

    setIsInitialized(true);

    if (!fromReportLanding) {
      loadReportResults(urlReportId);
    }
  }, [dynamicData, leftPanelReport, hasAppliedOnce]);

  const showToastMessage = (message: string, type: ToastType) => {
    setToastMessage(message);
    setToastStatus([type]);
    setShowToast(true);
  };

  const showWarningMessage = (message: string) => {
    showToastMessage(message, ToastType.Warning);
  };
  const [dataAsOf, setDataAsOf] = useState<string>(() => {
    if (leftPanelReport?.dataAsOf) {
      return dayjs.utc(leftPanelReport.dataAsOf).format('YYYY/MM/DD HH:mm:ss');
    }
    return dayjs.utc().format('YYYY/MM/DD HH:mm:ss');
  });

  const RIGHT_PANEL_HEADER_CONFIG: RightPanelConfig = {
    title: '',
    subTitle: `Data as of ${dataAsOf} UTC`,
    showSaveIcon: true,
    showColumnsIcon: true,
    showActionButton: false,
    showUploadIcon: true,
  };

  const handleColumnToggleAction = async (columnId: string) => {
    setSaveReportPayload((prev) => {
      const idx = columns.find((c) => c.field === columnId)?.index;
      if (typeof idx !== 'number') return prev;
      const next = new Set(prev.selectedCustomColumns);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return {...prev, selectedCustomColumns: Array.from(next)};
    });
  };

  const handleAction = async (
    actionKey: string,
    values: Record<string, any>
  ) => {
    if (actionKey === 'applyChanges') await handleApply(values);
    else if (actionKey === 'reset') handleReset();
  };

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let tries = 0;
    const tryAttach = () => {
      const btn = document.querySelector('[aria-label="Export"]');
      if (!btn || !runUserActivityExport) return false;
      btn.addEventListener('click', handleExport);
      cleanup = () => btn.removeEventListener('click', handleExport);
      return true;
    };
    if (!tryAttach()) {
      const id = window.setInterval(() => {
        tries += 1;
        if (tryAttach() || tries > 50) window.clearInterval(id);
      }, 50);
      return () => window.clearInterval(id);
    }
    return () => cleanup && cleanup();
  }, [
    runUserActivityExport,
    saveReportPayload,
    gridData,
    isInitialized,
    reportConfig,
  ]);

  const parseDateTimeField = (value: any): string | null => {
    // If a date is not entered into the input field, the value is an empty array (i.e. [])
    // If a date is entered into the input field, the value is a string (i.e. '2026/01/25 00:00:00')
    if (!value) {
      return null;
    }
    if (Array.isArray(value) && value.length === 0) {
      return null;
    }
    return value;
  };

  const handleApply = async (values: Record<string, any>) => {
    try {
      setHasAppliedOnce(true);
      setSelectedFilters(values);
      setInitialValues(values);

      // Action Types
      const containerActionTypes = Array.isArray(dynamicData?.actionTypes)
        ? dynamicData.actionTypes.map(() => true)
        : [];
      const effectiveActionTypes = Array.isArray(values.actionTypes)
        ? values.actionTypes
        : containerActionTypes;
      const selectedActionTypes = effectiveActionTypes
        .map((checked: boolean, idx: number) =>
          checked ? dynamicData?.actionTypes?.[idx]?.actionTypeID : null
        )
        .filter((id: string | null) => id !== null);

      // Users
      const containerUsers = Array.isArray(dynamicData?.userDetails)
        ? dynamicData.userDetails.map(() => true)
        : [];
      const effectiveUsers: boolean[] = Array.isArray(values.usersList)
        ? values.usersList
        : containerUsers;
      const selectedUsersList = effectiveUsers
        .map((checked: boolean, idx: number) =>
          checked ? dynamicData?.userDetails?.[idx]?.userId : null
        )
        .filter((id: number | null) => id !== null && !isNaN(id));

      const hasUsersList =
        Array.isArray(selectedUsersList) && selectedUsersList.length > 0;

      if (!hasUsersList) {
        showWarningMessage(ToastMessages.UserFieldValidationMessage);
        return;
      }

      const fromDateRaw = parseDateTimeField(values.fromDate);
      const toDateRaw = parseDateTimeField(values.toDate);

      if (!fromDateRaw && !toDateRaw) {
        showWarningMessage(ToastMessages.DateRangeValidationMessage);
        return;
      }

      if (!fromDateRaw && toDateRaw) {
        showWarningMessage(ToastMessages.DateRangeValidationMessage);
        return;
      }

      if (fromDateRaw && !toDateRaw) {
        showWarningMessage(ToastMessages.DateRangeValidationMessage);
        return;
      }

      if (fromDateRaw && toDateRaw) {
        const fromDateObj = dayjs(fromDateRaw);
        const toDateObj = dayjs(toDateRaw);

        if (fromDateObj.isAfter(toDateObj)) {
          showWarningMessage(ToastMessages.DateRangeValidationMessage);
          return;
        }
      }
      setGridData(null);
      setLoading(true);
      setDataAsOf(dayjs.utc().format('YYYY/MM/DD HH:mm:ss'));

      const fromDate =
        fromDateRaw ??
        new Date(
          new Date().setFullYear(new Date().getFullYear() - 10)
        ).toISOString();
      const toDate = toDateRaw ?? new Date().toISOString();

      const savePayload: UserActivitySaveRequestPayload = {
        reportTypeCode: REPORT_TYPE_CODE,
        selected_ActionTypeID: selectedActionTypes,
        selected_UserID: selectedUsersList,
        fromDateTime: fromDate,
        toDateTime: toDate,
        selectedCustomColumns: saveReportPayload.selectedCustomColumns ?? [],
        pageNumber: -1,
        pageSize: -1,
        isAscending: 'desc',
        sortingColumn: 'DocumentDateTime',
        dataAsOf: dayjs
          .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
          .toISOString(),
        showCurrentlyLoggedInUsers:
          selectedOption?.value === 'loginHistory' ? 0 : 1,
        downloadRequestedOn: new Date().toISOString(),
        include_Assignment_Detail: 0,
        reportID: 0,
        documentID: 0,
        selected_SearchID: [],
        saveSearchID: 0,
        showDailyReport: false,
        timeOffset: 0,
        selectedReviewers: [],
        selectedAssignments: [],
        selectedCustodians: [],
        selectedFoldersOrGroups: [],
        selectedAssignmentClassification: [],
        selectedTagClassification: [],
        selectedTags: [],
        searchTermReportType: '',
        rowNumber: 0,
        assignmentFlag: false,
        selectedSecurityGroups: [],
        selectedDocumentTypes: [],
        selectedRedactionTags: [],
        selectedCommentTypes: [],
        selectedProductions: [],
        isBulkAction: 0,
        selectedSource: '',
        selectedSourceID: [],
        selectedCustomColumnName: [],
        selectedReviewAssignments: [],
        selectedQCAssignment: [],
        displayBy: 0,
        documentsNotinAnyAssignment: false,
        exportFields: [],
        exportData: [],
        style: '',
        field: '',
        text: '',
        dateStyle: '',
        reportName: '',
        docID: '',
        bulkActionSelectionCriteria: [],
        hugeSearchId: '',
        batchLevel: '',
        filterDocumentGrid: '',
        bulkActionABMAssignment: [],
        bulkActionABMDocument: [],
        navigationSourceContext: '',
        navigationDestinationContext: '',
        isSelectedAll: false,
        subActionType: '',
        isShared: false,
        isSavedSearchSource: false,
      };

      const saveResponse = await saveUserActivityReportData(savePayload);

      setSaveReportPayload((prev) => ({
        ...prev,
        ...savePayload,
        reportID: saveResponse.reportID || 0,
        fromDateTime: savePayload.fromDateTime ?? '',
        toDateTime: savePayload.toDateTime ?? '',
      }));

      setReportId(saveResponse.reportID);
      setNotificationId(saveResponse.notificationID);
    } catch (e) {
      console.error('Error initializing report:', e);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setHasAppliedOnce(false);
    setSelectedFilters(initialValues ?? {});
    setGridData(null);
  };

  const [isExporting, setIsExporting] = useState<boolean>(false);
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const selectedActionTypes = (selectedFilters.actionTypes || [])
        .map((checked: boolean, idx: number) =>
          checked ? dynamicData?.actionTypes[idx]?.actionTypeID : null
        )
        .filter((id: string | null) => id !== null);

      const selectedUsersList = (selectedFilters.usersList || [])
        .map((s: any, idx: number) => {
          if (typeof s === 'object' && s?.userId) return Number(s.userId);
          if (typeof s === 'boolean' && s)
            return Number(dynamicData?.userDetails[idx]?.userId);
          if (typeof s === 'string' || typeof s === 'number') return Number(s);
          return null;
        })
        .filter((id: number | null) => id !== null && !isNaN(id));

      const payload: ExportUserActivityReportRequestPayload = {
        selectionCriteriaProgress: {
          reportTypeCode: REPORT_TYPE_CODE,
          selected_ActionTypeID: selectedActionTypes,
          selected_UserID: selectedUsersList,
          fromDateTime: saveReportPayload.fromDateTime || '',
          toDateTime: saveReportPayload.toDateTime || '',
          selectedCustomColumns: saveReportPayload.selectedCustomColumns ?? [],
          reportID: saveReportPayload.reportID || 0,
          sortingColumn: 'DocumentDateTime',
          isAscending: 'desc',
          downloadRequestedOn: new Date().toISOString(),
          dataAsOf: dayjs
            .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
            .toISOString(),
          docID: '0',
          selected_SearchID: [],
          timeOffset: 0,
          documentID: 0,
          include_Assignment_Detail: 0,
          saveSearchID: 0,
          showDailyReport: false,
          selectedReviewers: [],
          selectedAssignments: [],
          selectedCustodians: [],
          selectedFoldersOrGroups: [],
          selectedAssignmentClassification: [],
          selectedTagClassification: [],
          selectedTags: [],
          searchTermReportType: '',
          pageNumber: -1,
          pageSize: -1,
          rowNumber: 0,
          assignmentFlag: false,
          selectedSecurityGroups: [],
          selectedDocumentTypes: [],
          selectedRedactionTags: [],
          selectedCommentTypes: [],
          selectedProductions: [],
          isBulkAction: 0,
          selectedSource: '',
          selectedSourceID: [],
          selectedCustomColumnName: [],
          selectedReviewAssignments: [],
          selectedQCAssignment: [],
          displayBy: 0,
          documentsNotinAnyAssignment: false,
          exportFields: [],
          exportData: [],
          style: '',
          field: '',
          text: '',
          dateStyle: '',
          reportName: '',
          bulkActionSelectionCriteria: [],
          hugeSearchId: '',
          batchLevel: '',
          filterDocumentGrid: '',
          bulkActionABMAssignment: [],
          bulkActionABMDocument: [],
          navigationSourceContext: '',
          navigationDestinationContext: '',
          isSelectedAll: false,
          subActionType: '',
          showCurrentlyLoggedInUsers: 0,
          isShared: false,
          isSavedSearchSource: false,
        },
        actionType: 'EXPORTREPORT',
      };

      await runUserActivityExport(payload);
      showToastMessage(ToastMessages.exportHasStarted, ToastType.Success);
    } catch (err) {
      console.error('Export failed:', err);
      showToastMessage(ToastMessages.exportHasFailed, ToastType.Error);
    } finally {
      setIsExporting(false);
    }
  };

  const getReportsConfig = () => {
    const currentValues = selectedFilters ?? initialValues;
    return mergeApiDataIntoConfig(
      reportConfig?.config,
      dynamicData,
      currentValues
    );
  };

  const getReportTitle = () =>
    (reportConfig &&
      (getReportNameFromUrl(window.location.search)
        ? getReportNameFromUrl(window.location.search)
        : reportConfig.title)) ??
    '';

  const loadReportResults = async (
    reportId: number,
    sort?: {columnName?: string; direction?: string},
    pageNumber?: number,
    pageSize?: number
  ) => {
    setLoading(true);
    try {
      const gridPayload: UserActivityReportRequestBody = {
        reportID: reportId,
        pageNumber: pageNumber ?? 1,
        pageSize: pageSize ?? pageModel.pageSize,
        sortingColumn: sort?.columnName ?? 'DocumentDateTime',
        direction: sort?.direction ?? 'desc',
      };

      const gridResponse = await getReportGridData(gridPayload);
      const rows = Array.isArray(gridResponse?.data) ? gridResponse.data : [];

      setPageModel((prev) => {
        return {
          ...prev,
          totalRecords: gridResponse.totalDisplayRecords,
        };
      });

      setGridData(
        rows.length === 0
          ? ({
              totalDisplayRecords: 0,
              data: [],
              noDataMessage: t('backgroundTasks.messageForNoGridData'),
            } as any)
          : gridResponse
      );
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
      setNotificationId(null);
      setReportId(null);
    }
  };

  const resolveSortColumn = (columnName?: string | null): string => {
    const DEFAULT_COLUMN = 'DocumentDateTime';

    // Validate the input
    if (!columnName) {
      return DEFAULT_COLUMN;
    }

    // Find the column by name
    const sortableColumn = columns.find((i) => i.field === columnName);
    if (sortableColumn) {
      return sortableColumn.apiName;
    }

    // Could not resolve, return default
    return DEFAULT_COLUMN;
  };

  const resolveSortDirection = (direction?: string | null): string => {
    const DIRECTION_ASCENDING = 'asc';
    const DIRECTION_DESCENDING = 'desc';

    // Validate the input
    if (!direction) {
      return DIRECTION_DESCENDING;
    }

    // Attempt to resolve the direction to a known value
    if (direction === DIRECTION_ASCENDING) {
      return DIRECTION_ASCENDING;
    }
    if (direction === DIRECTION_DESCENDING) {
      return DIRECTION_DESCENDING;
    }

    // Could not resolve, return default
    return DIRECTION_DESCENDING;
  };

  const handlePageChange = (
    pageNumber?: number | null,
    pageSize?: number | null
  ) => {
    setPageModel((prev) => ({
      ...prev,
      pageNumber: pageNumber ?? prev.pageNumber,
      pageSize: pageSize ?? prev.pageSize,
    }));
  };

  const handleSortChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  // Reload whenever sorting or paging changes. totalRecords is deliberately not
  // a dependency, as it is written back after each response.
  useEffect(() => {
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      return;
    }

    // Parse a report ID
    let id = 0;
    if (reportId) {
      id = reportId;
    } else if (urlReportId) {
      id = urlReportId;
    } else if (saveReportPayload.reportID) {
      id = saveReportPayload.reportID;
    }
    if (!id) {
      return;
    }

    // Parse sort
    const column = resolveSortColumn(sortModel[0]?.field);
    const direction = resolveSortDirection(sortModel[0]?.sort);
    const sort = {columnName: column, direction};

    loadReportResults(id, sort, pageModel.pageNumber, pageModel.pageSize);
  }, [sortModel, pageModel.pageNumber, pageModel.pageSize]);

  return (
    <>
      {!isInitialized ? (
        <ArrLoadingSpinner
          show={!isInitialized || loading || isWaitingForBackgroundJob}
          overlay={true}
          isOpen={true}
        />
      ) : (
        <StandardReports
          title={getReportTitle()}
          showInfoIcon={reportConfig?.showInfoIcon}
          reportsConfig={getReportsConfig()}
          selectedOption={selectedOption}
          onOptionChange={setSelectedOption}
          onAction={handleAction}
          onColumnToggleAction={handleColumnToggleAction}
          data={gridData}
          getRowId={(row: any) => row.id}
          isNoData={gridData?.totalDisplayRecords === 0}
          loading={loading}
          columns={columns}
          infoContent={reportConfig?.infoContent}
          mapRows={(resp: any) =>
            (Array.isArray(resp?.data) ? resp.data : []).map(
              (row: any, index: number) => {
                const {
                  actionName,
                  actionOn,
                  documentDateTime,
                  documentID,
                  userLocation,
                  userName,
                } = row;
                return {
                  id: index,
                  users: userName ?? '',
                  docID: documentID ?? '',
                  action: actionName ?? '',
                  actionOn: actionOn ?? '',
                  locationOfUser: userLocation ?? '',
                  dateTime: documentDateTime ?? '',
                };
              }
            )
          }
          rightPanelHeaderConfig={RIGHT_PANEL_HEADER_CONFIG}
          saveReportConfig={saveReportPayload}
          savedData={leftPanelReport}
          gridConfig={{
            // Sorting
            sortModel: sortModel,
            onSortModelChange: (model) => handleSortChange(model),
            // Paging
            totalRecords: pageModel.totalRecords,
            itemsPerPage: pageModel.pageSize,
            currentPage: pageModel.pageNumber,
            pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
            showPerPageOptions: true,
            setItemsPerPage: (pageSize) => handlePageChange(null, pageSize),
            onPageChange: (pageNumber, pageSize) =>
              handlePageChange(pageNumber, pageSize),
          }}
          skipDateValidation={true}
        />
      )}

      {notificationId && reportId && (
        <ArrWaitToCompleteModalWrapper
          notificationId={notificationId}
          reportId={reportId}
          onComplete={async (completedReportId) => {
            await loadReportResults(completedReportId);
          }}
          loader
          showClose
        />
      )}

      {showToast && (
        <div data-testid="toast-notification">
          <ArrToastNotification
            message={toastMessage}
            status={toastStatus}
            onClose={() => setShowToast(false)}
          />
        </div>
      )}
    </>
  );
};

export default UserActivityContainer;
